import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import { CustomerProfile, MailFlags, ProfileService } from '@le_cedre/portal-lib';
import { atLeastOnePhoneRequired, landLinePattern, mailPattern, mobilePattern } from '@shared/forms/validators';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EmailValidationService } from '../service/email-validation.service';
import { UserService } from '@app/services/user.service';
import { UserProfile, Civility } from '@app/core/models/user.interface';
import { NgFor, NgIf, NgClass, AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-portal-first-log',
    template: `
        <div class="profile">
            <div class="profile-content">
                <form class="credentials-form" (ngSubmit)="onSubmit()" [formGroup]="updateUserCredentialsForm">
                    <h1 [innerHTML]="'FIRST_LOG_TITLE' | translate" class="profile-title"></h1>
                    <div class="credentials-element field-civility">
                        <label class="form-label__required" [innerHTML]="'FORM_LABEL_CIVILITY' | translate"></label>
                        <select
                            data-cy="FIRST_LOG_SELECT_CIVILITY"
                            [class.error-field]="isFieldValid('civility')"
                            class="credentials-element__select"
                            name="civility"
                            formControlName="civility"
                        >
                            <option value=""></option>
                            <option
                                *ngFor="let item of civilities$ | async"
                                [selected]="userProfile?.user?.civility === item.name"
                            >
                                {{ item.name | translate }}
                            </option>
                        </select>
                        <div class="error-message" *ngIf="isFieldValid('civility')">
                            <span
                                *ngIf="updateUserCredentialsForm.get('civility').errors?.required"
                                [innerHTML]="'FORM_ERROR_REQUIRED' | translate"
                            ></span>
                        </div>
                    </div>
                    <div class="credentials-element">
                        <label class="form-label__required" [innerHTML]="'FORM_LABEL_LAST_NAME' | translate"></label>
                        <input
                            data-cy="FIRST_LOG_INPUT_LAST_NAME"
                            [class.error-field]="isFieldValid('lastName')"
                            class="credentials-element__input"
                            type="text"
                            name="lastName"
                            formControlName="lastName"
                        />
                        <div class="error-message" *ngIf="isFieldValid('lastName')">
                            <span
                                *ngIf="updateUserCredentialsForm.get('lastName').errors?.required"
                                [innerHTML]="'FORM_ERROR_REQUIRED' | translate"
                            ></span>
                        </div>
                    </div>
                    <div class="credentials-element">
                        <label class="form-label__required" [innerHTML]="'FORM_LABEL_FIRST_NAME' | translate"></label>
                        <input
                            data-cy="FIRST_LOG_INPUT_FIRST_NAME"
                            [class.error-field]="isFieldValid('firstName')"
                            class="credentials-element__input"
                            type="text"
                            name="firstName"
                            formControlName="firstName"
                        />
                        <div class="error-message" *ngIf="isFieldValid('firstName')">
                            <span
                                *ngIf="updateUserCredentialsForm.get('firstName').errors?.required"
                                [innerHTML]="'FORM_ERROR_REQUIRED' | translate"
                            ></span>
                        </div>
                    </div>
                    <div class="credentials-element">
                        <label class="form-label__required" [innerHTML]="'FORM_LABEL_EMAIL' | translate"></label>
                        <input
                            data-cy="FIRST_LOG_INPUT_EMAIL"
                            [class.error-field]="isFieldValid('email')"
                            class="credentials-element__input"
                            type="text"
                            name="email"
                            formControlName="email"
                        />
                        <div class="error-message" *ngIf="isFieldValid('email')">
                            <span
                                *ngIf="updateUserCredentialsForm.get('email').errors?.required"
                                [innerHTML]="'FORM_ERROR_REQUIRED' | translate"
                            ></span>
                            <span
                                *ngIf="updateUserCredentialsForm.get('email').errors?.pattern"
                                [innerHTML]="'FORM_ERROR_EMAIL' | translate"
                            ></span>
                        </div>
                    </div>
                    <p class="credentials-hint" [innerHTML]="'FORM_HINT_PHONE_REQUIRED' | translate"></p>
                    <div class="credentials-element">
                        <label
                            [ngClass]="
                                updateUserCredentialsForm.get('landlineNumber').value
                                    ? 'form-label'
                                    : 'form-label__required'
                            "
                            [innerHTML]="'FORM_LABEL_MOBILE_PHONE_NUMBER' | translate"
                        ></label>
                        <input
                            data-cy="FIRST_LOG_INPUT_MOBILE_PHONE"
                            [class.error-field]="isFieldValid('mobilePhoneNumber')"
                            class="credentials-element__input"
                            type="text"
                            name="mobilePhoneNumber"
                            formControlName="mobilePhoneNumber"
                        />
                        <div class="error-message" *ngIf="isFieldValid('mobilePhoneNumber')">
                            <span
                                *ngIf="updateUserCredentialsForm.get('mobilePhoneNumber').errors?.pattern"
                                [innerHTML]="'FORM_ERROR_PHONE_NUMBER' | translate"
                            ></span>
                        </div>
                        <div class="error-message phone-group-error" *ngIf="phoneGroupHasError">
                            <span [innerHTML]="'FORM_HINT_PHONE_REQUIRED' | translate"></span>
                        </div>
                    </div>
                    <div class="credentials-element">
                        <label
                            [ngClass]="
                                updateUserCredentialsForm.get('mobilePhoneNumber').value
                                    ? 'form-label'
                                    : 'form-label__required'
                            "
                            [innerHTML]="'FORM_LABEL_FIX_PHONE_NUMBER' | translate"
                        ></label>
                        <input
                            data-cy="FIRST_LOG_INPUT_LANDLINE"
                            [class.error-field]="isFieldValid('landlineNumber')"
                            class="credentials-element__input"
                            type="text"
                            name="landlineNumber"
                            formControlName="landlineNumber"
                        />
                        <div class="error-message" *ngIf="isFieldValid('landlineNumber')">
                            <span
                                *ngIf="updateUserCredentialsForm.get('landlineNumber').errors?.pattern"
                                [innerHTML]="'FORM_ERROR_PHONE_NUMBER' | translate"
                            ></span>
                        </div>
                    </div>
                    <div class="credentials-element field-submit">
                        <button
                            data-cy="FIRST_LOG_BTN_SUBMIT"
                            class="cedre-primary-btn submit-button"
                            type="submit"
                            value="UpdateInfos"
                            [innerHTML]="'FORM_LABEL_SUBMIT_CHANGES' | translate"
                        ></button>
                    </div>
                </form>
            </div>
        </div>
    `,
    styleUrls: ['./first-log.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, NgFor, NgIf, NgClass, AsyncPipe, TranslateModule],
})
export class FirstLogComponent implements OnInit, OnDestroy {
    customerProfile$: Observable<CustomerProfile>;
    mailFlags$: Observable<MailFlags>;
    private destroyed$ = new Subject<void>();
    userProfile: UserProfile;
    updateUserCredentialsForm: UntypedFormGroup;
    isValid: boolean;
    isRefused: boolean;
    isLoading: boolean;
    civilities$: Promise<Civility[]>;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private profile: ProfileService,
        private email: EmailValidationService,
        private userService: UserService
    ) {
        this.customerProfile$ = this.profile.customerProfile$;
        this.mailFlags$ = this.email.flags$;
    }

    ngOnInit() {
        this.civilities$ = this.userService.getCivilities();

        const required = Validators.required;
        this.updateUserCredentialsForm = this.formBuilder.group(
            {
                civility: ['', required],
                lastName: ['', required],
                firstName: ['', required],
                email: ['', [required, mailPattern]],
                mobilePhoneNumber: ['', mobilePattern],
                landlineNumber: ['', landLinePattern],
            },
            { validators: atLeastOnePhoneRequired('mobilePhoneNumber', 'landlineNumber') }
        );

        this.mailFlags$.pipe(takeUntil(this.destroyed$)).subscribe((mailFlagsSnapshot) => {
            this.isValid = mailFlagsSnapshot.isValid;
            this.isRefused = mailFlagsSnapshot.isRefused;
            this.isLoading = mailFlagsSnapshot.isLoading;
        });

        this.customerProfile$.pipe(takeUntil(this.destroyed$)).subscribe(async (customerProfileSnapshot) => {
            if (Object.keys(customerProfileSnapshot).length) {
                const userDatahubId = customerProfileSnapshot.ExternalIds.find(
                    (externalId) => externalId.Source === 'Datahub'
                ).SourceId;
                this.userProfile = (await this.userService.getProfileById(userDatahubId)).profile;
                this.hydrateCustomerProfileCredentials();
            }
        });
    }

    hydrateCustomerProfileCredentials() {
        const user = this.userProfile.user;
        this.updateUserCredentialsForm.patchValue({
            civility: user.civility,
            lastName: user.lastname,
            firstName: user.firstname,
            email: user.email,
            mobilePhoneNumber: this.userProfile.mobile,
            landlineNumber: this.userProfile.phone,
        });
    }

    onSubmit() {
        if (this.updateUserCredentialsForm.valid) {
            this.profile.updateByToken({
                civility: this.updateUserCredentialsForm.get('civility').value,
                firstname: this.updateUserCredentialsForm.get('firstName').value,
                lastname: this.updateUserCredentialsForm.get('lastName').value,
                mobile: this.updateUserCredentialsForm.get('mobilePhoneNumber').value || '',
                phone: this.updateUserCredentialsForm.get('landlineNumber').value || '',
            });
        } else {
            this.validateAllFormFields(this.updateUserCredentialsForm);
        }
    }

    isFieldValid(field: string) {
        return this.updateUserCredentialsForm.get(field).errors && this.updateUserCredentialsForm.get(field).dirty;
    }

    validateAllFormFields(formGroup: UntypedFormGroup) {
        Object.keys(formGroup.controls).forEach((field) => {
            const control = formGroup.get(field);
            if (control instanceof UntypedFormControl) {
                control.markAsDirty({ onlySelf: true });
            } else if (control instanceof UntypedFormGroup) {
                this.validateAllFormFields(control);
            }
        });
    }

    get phoneGroupHasError(): boolean {
        const mobileDirty = this.updateUserCredentialsForm.get('mobilePhoneNumber').dirty;
        const landlineDirty = this.updateUserCredentialsForm.get('landlineNumber').dirty;
        const groupInvalid = !!this.updateUserCredentialsForm.errors?.atLeastOnePhoneRequired;

        return groupInvalid && (mobileDirty || landlineDirty);
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
